import { isLimitedOfferBenefit } from '../../utils/events';
import { getPlayerLimitedOfferStock } from '../../utils/limitedOffer';

import type { EventTriggerPopupData } from './types';

const autoPopupSuppressionKey = (userEventTriggerId: string) =>
  `eventTrigger:${userEventTriggerId}:autoPopupSuppressed`;

/**
 * Why an event, once the player has seen it, must never be force-shown again this session — it
 * stays in the lobby and opens only on a deliberate widget click.
 *
 * Evaluated when a popup opens, first match wins, and the reason is what gets stored, so a
 * session's suppressions stay readable. A new case is one entry here and nothing else.
 */
const AUTO_POPUP_SUPPRESSION_RULES: ReadonlyArray<{
  reason: string;
  matches: (event: EventTriggerPopupData) => boolean;
}> = [
  {
    // A sold-out limited offer has nothing left to sell — pushing it at the player is pure friction.
    reason: 'limitedOfferSoldOut',
    matches: (event) => isLimitedOfferBenefit(event.benefit) && !!getPlayerLimitedOfferStock(event)?.isSoldOut,
  },
];

/**
 * Session-scoped, and deliberately not cleared on reset: a suppression is about what the player has
 * already been shown, which a re-login does not undo. Storage failures (Safari private mode) read as
 * "not suppressed" — one extra popup beats a popup path that throws.
 */
export const suppressAutoPopupIfNeeded = (event: EventTriggerPopupData): void => {
  const reason = AUTO_POPUP_SUPPRESSION_RULES.find((rule) => rule.matches(event))?.reason;
  if (!reason) return;

  try {
    sessionStorage.setItem(autoPopupSuppressionKey(event.userEventTriggerId), reason);
  } catch {
    // ignore — see above
  }
};

export const isAutoPopupSuppressed = (userEventTriggerId: string): boolean => {
  try {
    return !!sessionStorage.getItem(autoPopupSuppressionKey(userEventTriggerId));
  } catch {
    return false;
  }
};
